import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';
import { Shift, ApprovalStatus } from '../../components/shiftcard/ShiftCard';
import { GigShiftGroup, RawGigShiftDay } from '../../types/gigShiftTypes';
import { groupGigShifts } from '../../utils/gigShiftUtils';

// ========================================
// TYPES
// ========================================
export type MyShiftsFilter = 'all' | 'past' | 'upcoming' | 'range';

// Shape of a my-shift row as returned by the API.
// The backend exposes the Request_Detail status as requestStatus.
type MyShiftResponse = {
    id?: string;
    requestId?: number | string;
    requestSeqId?: number | string;
    shiftId?: string;
    requestShiftId?: string;

    title?: string;
    shiftType?: string;
    location?: string;
    date?: string;
    requestDt?: string;
    requestDate?: string;
    startTime?: string;
    endTime?: string;
    rate?: string;
    description?: string;
    notes?: string;
    postedBy?: string;

    claimedBy?: string;
    claimedById?: string;
    claimedByUserId?: string;
    claimedByUserNm?: string;

    requestStatus?: Shift['status'];
    rejectionNotes?: string;

    // New fields for WKR dual approval
    posterSupervisorId?: string;
    posterSupervisorName?: string;
    posterSupervisorApprovalStatus?: ApprovalStatus;
    claimerSupervisorId?: string;
    claimerSupervisorName?: string;
    claimerSupervisorApprovalStatus?: ApprovalStatus;
};

interface MyShiftsState {
    shifts: Shift[];
    gigShifts: GigShiftGroup[];
    loading: boolean;
    error: string | null;
    filter: MyShiftsFilter;
}

const initialState: MyShiftsState = {
    shifts: [],
    gigShifts: [],
    loading: false,
    error: null,
    filter: 'all',
};

// ========================================
// HELPERS
// ========================================
// My shifts can have multiple rows per requestId (one per person), so build a
// composite "{requestId}-{requestSeqId}" id to keep React keys unique.
const deriveId = (shift: MyShiftResponse): string => {
    const baseRaw = shift.requestId ?? shift.id ?? shift.shiftId ?? shift.requestShiftId;
    const base = baseRaw !== undefined && baseRaw !== null ? String(baseRaw) : '';
    const seq = shift.requestSeqId !== undefined && shift.requestSeqId !== null
        ? String(shift.requestSeqId)
        : '';
    if (base && seq) return `${base}-${seq}`;
    return base || seq;
};

const normalizeShifts = (payload: MyShiftResponse[]): Shift[] =>
    (payload ?? []).map((s) => ({
        ...s,
        id: deriveId(s),
        date: s.date ?? s.requestDt ?? s.requestDate ?? '',
        status: s.requestStatus,
        claimedBy: s.claimedByUserNm ?? s.claimedBy,
        claimedById: s.claimedByUserId ?? s.claimedById,
        rejectionNotes: s.requestStatus === 'DEN' ? s.rejectionNotes : undefined,
        // Map dual approval fields for WKR shifts
        dualApproval: s.shiftType === 'WKR' && s.posterSupervisorApprovalStatus ? {
            posterSupervisorId: s.posterSupervisorId,
            posterSupervisorName: s.posterSupervisorName,
            posterSupervisorApprovalStatus: s.posterSupervisorApprovalStatus,
            claimerSupervisorId: s.claimerSupervisorId,
            claimerSupervisorName: s.claimerSupervisorName,
            claimerSupervisorApprovalStatus: s.claimerSupervisorApprovalStatus,
        } : undefined,
    })) as Shift[];

// ========================================
// ASYNC THUNKS
// ========================================
/**
 * Fetch the current user's claimed shifts.
 * Uses: GET /api/shift/myShifts/{siteCd}/{userId}?filter=all|thisweek|today|range&statuses=PND&statuses=APP
 * When filter=range, startDate/endDate (YYYY-MM-DD) bound the results server-side.
 */
export const fetchMyShifts = createAsyncThunk(
    'myShifts/fetchMyShifts',
    async (
        {
            siteCd,
            userId,
            filter = 'all',
            statuses = [],
            startDate,
            endDate,
        }: {
            siteCd: string;
            userId: string;
            filter?: MyShiftsFilter;
            statuses?: string[];
            startDate?: string;
            endDate?: string;
        },
        { rejectWithValue }
    ) => {
        try {
            const params = new URLSearchParams();
            params.append('filter', filter);
            statuses.forEach((status) => {
                if (status) {
                    params.append('statuses', status);
                }
            });
            if (filter === 'range') {
                if (startDate) params.append('startDate', startDate);
                if (endDate) params.append('endDate', endDate);
            }

            const response = await API_FETCH(
                `${ENDPOINTS.GET_MY_SHIFTS}/${siteCd}/${userId}?${params.toString()}`
            );
            console.log('✅ My Shifts API Response:', response);
            return response as MyShiftResponse[] | { shifts?: MyShiftResponse[]; gigShifts?: RawGigShiftDay[] };
        } catch (error: any) {
            console.error('❌ My Shifts API Error:', error);
            return rejectWithValue(error?.message || 'Failed to fetch my shifts');
        }
    }
);

// ========================================
// SLICE
// ========================================
const myShiftsSlice = createSlice({
    name: 'myShifts',
    initialState,
    reducers: {
        setFilter: (state, action) => {
            state.filter = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMyShifts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMyShifts.fulfilled, (state, action) => {
                state.loading = false;
                const payload = action.payload as
                    | MyShiftResponse[]
                    | { shifts?: MyShiftResponse[]; gigShifts?: RawGigShiftDay[] };
                if (Array.isArray(payload)) {
                    state.shifts = normalizeShifts(payload);
                    state.gigShifts = [];
                } else {
                    state.shifts = normalizeShifts(payload?.shifts ?? []);
                    state.gigShifts = groupGigShifts(payload?.gigShifts);
                }
            })
            .addCase(fetchMyShifts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { setFilter, clearError } = myShiftsSlice.actions;
export default myShiftsSlice.reducer;